import {inject} from '@angular/core';
import {Actions, ofType} from '@ngrx/effects';
import {Action, Store} from '@ngrx/store';
import {IS_BROWSER} from '@shared/constants/is-browser.token';
import {createFunctionalEffect} from '@shared/utils/effects.helper';
import {AuthenticationActions} from '@state/authentication/authentication.actions';
import {authenticationFeature} from '@state/authentication/authentication.feature';
import {HistoryActions} from '@state/history';
import {
  createFavoriteToggleEffect,
  createSyncEffect,
} from '@state/subject/dossier-effect.factories';
import {SubjectActions} from '@state/subject/subject.actions';
import {SubjectApi} from '@state/subject/subject.api';
import {
  EApiNotification,
  mapOdApiActions,
} from '@utils/map-od-api-actions.function';
import {filter, map, switchMap, takeUntil, withLatestFrom} from 'rxjs';

import {BranchActions} from './branch.actions';
import {BranchApi} from './branch.api';
import {branchFeature} from './branch.feature';
import {IBranchRequest} from './branch-state.interface';

const SYNC_POLL_INTERVAL_MS = 3000;

export namespace BranchEffects {
  export const getBranch$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(BranchApi)) =>
      actions$.pipe(
        ofType(BranchActions.getBranch.requested),
        switchMap(({branchId}) =>
          api
            .getBranch(branchId)
            .pipe(
              mapOdApiActions(
                BranchActions.getBranch,
                EApiNotification.OnErrorExceptNotFound
              ),
              takeUntil(actions$.pipe(ofType(BranchActions.clearBranch)))
            )
        )
      )
  );

  export const loadBranchRelatedData$ = createFunctionalEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      isBrowser = inject(IS_BROWSER)
    ) =>
      actions$.pipe(
        ofType(BranchActions.getBranch.succeeded),
        withLatestFrom(
          store.select(authenticationFeature.selectIsAuthenticated)
        ),
        switchMap(([{payload}, isAuthenticated]) => {
          const resultActions: Action[] = [];

          if (isBrowser) {
            resultActions.push(
              SubjectActions.getLinkedSubjects.requested({
                subjectId: payload.id,
              })
            );
          }

          if (isAuthenticated) {
            resultActions.push(
              BranchActions.getBranchFavorites.requested({
                subjectId: payload.id,
              }),
              HistoryActions.saveToHistory.requested({subjectId: payload.id})
            );
          }

          return resultActions;
        })
      )
  );

  export const getBranchFavorites$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(SubjectApi)) =>
      actions$.pipe(
        ofType(BranchActions.getBranchFavorites.requested),
        switchMap(({subjectId}) =>
          api.getSubjectFavorites(subjectId).pipe(
            map((value) => ({data: value})),
            mapOdApiActions(BranchActions.getBranchFavorites),
            takeUntil(actions$.pipe(ofType(BranchActions.clearBranch)))
          )
        )
      )
  );

  export const markFavorite$ = createFavoriteToggleEffect({
    trigger: BranchActions.markFavorite,
    failed: BranchActions.markFavoriteFailed,
    request: (api, subjectId) => api.markFavorite(subjectId),
    successMessage: 'dossier.favorite-added',
  });

  export const unmarkFavorite$ = createFavoriteToggleEffect({
    trigger: BranchActions.unmarkFavorite,
    failed: BranchActions.unmarkFavoriteFailed,
    request: (api, subjectId) => api.unmarkFavorite(subjectId),
    successMessage: 'dossier.favorite-removed',
  });

  export const syncBranch$ = createSyncEffect({
    syncActions: BranchActions.branchSync,
    clearAction: BranchActions.clearBranch,
    pollIntervalMs: SYNC_POLL_INTERVAL_MS,
  });

  export const syncBranchSucceeded$ = createFunctionalEffect(
    (actions$ = inject(Actions)) =>
      actions$.pipe(
        ofType(BranchActions.branchSync.succeeded),
        map(({payload}) =>
          BranchActions.getBranch.requested({
            branchId: payload.subjectId.toString(),
          })
        )
      )
  );

  export const reloadBranchOnLogin$ = createFunctionalEffect(
    (actions$ = inject(Actions), store = inject(Store)) =>
      actions$.pipe(
        ofType(AuthenticationActions.loginConfirm.succeeded),
        withLatestFrom(store.select(branchFeature.selectBranchRequest)),
        map(([, request]) => request),
        filter((request): request is IBranchRequest => !!request),
        map((request) => BranchActions.getBranch.requested(request))
      )
  );

  export const reloadBranchOnLogout$ = createFunctionalEffect(
    (actions$ = inject(Actions), store = inject(Store)) =>
      actions$.pipe(
        ofType(AuthenticationActions.logout),
        withLatestFrom(store.select(branchFeature.selectBranchRequest)),
        map(([, request]) => request),
        filter((request): request is IBranchRequest => !!request),
        switchMap((request) => [
          BranchActions.clearBranch(),
          BranchActions.getBranch.requested(request),
        ])
      )
  );
}
